import { DragIndicator, ForkRight, WarningAmberRounded } from '@mui/icons-material';
import { Permissions, useScopeChecker } from 'src/rbac';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import {
  Avatar,
  AvatarGroup,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import React, { useState, type InputHTMLAttributes } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SchedulerProgressNoteDueGlyph } from 'src/components/progressNoteDue/ProgressNoteDueIndicators';
import type { ProgressNoteDue } from 'src/types/progressNoteDue';
import { AddNoteIcon } from 'src/components/therapyPlanning_2.0/commonComponent/therapy-planning-icons';
import { appointmentBookFlag, isSplitMinEnabled } from 'src/config';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { formatPatientData, getPayerCateGroup } from 'src/layouts/components/utils-method';
import { DefaultSkillIcon } from 'src/pages/therapist-dashboard/dashboard-icon';
import {
  getTherapistTaskNoteAction,
  resetCreateTherapistTaskNote,
} from 'src/redux/actions/therapySchedulingSkillList';
import calendar from '../../../../icons/untitled-ui/duocolor/calendar-plus.svg';
import divide from '../../../../icons/untitled-ui/duocolor/divide.svg';
import trash from '../../../../icons/untitled-ui/duocolor/trash.svg';
import { PencilIcon, PriorityHigh } from './schedule-icon';
import { TaskTypeSvg } from './task-type-svg';
import { taskTestId } from '../../test-ids';

type Props = {
  patient: any;
  handleToRescheduleTask: any;
  setTaskSelectedId: any;
  taskSelectedId: any;
  setCurrentPatientTask?: any;
  setOpenTaskDeleteModal?: any;
  handleOpenTaskNote: any;
  setIsEditNote: any;
  handleOpenAddTaskFormFromList: any;
  handleSpitTask: (arg: any) => void;
  editUnassignedTaskMin: (arg: any) => void;
  /** THIN-8145: the scheduler's selected date, YYYY-MM-DD. */
  selectedDate?: string;
};

const skillsList = [
  {
    skillName: 'Shower Transfers',
    icon: '/public/assets/skills-icon/Shower Task.svg',
  },
  {
    skillName: 'Don/Doff Socks',
    icon: '/public/assets/skills-icon/Don_Doff Socks.svg',
  },
  {
    skillName: 'Don/Doff Shoes',
    icon: '/public/assets/skills-icon/Don_Doff Shoes.svg',
  },
  {
    skillName: 'LB Dressing',
    icon: '/public/assets/skills-icon/LB Dressing.svg',
  },
  {
    skillName: 'UB Dressing',
    icon: '/public/assets/skills-icon/UB Dressing.svg',
  },
  {
    skillName: 'Shower Task',
    icon: '/public/assets/skills-icon/Shower Task.svg',
  },
  {
    skillName: 'Bathing Task',
    icon: '/public/assets/skills-icon/Bathing Task.svg',
  },

  {
    skillName: 'Medication Management',
    icon: '/public/assets/skills-icon/Medication Management.svg',
  },
  {
    skillName: 'Meal Prep',
    icon: '/public/assets/skills-icon/Meal Prep.svg',
  },
  {
    skillName: 'Financial Management',
    icon: '/public/assets/skills-icon/Financial Management.svg',
  },
  {
    skillName: 'Self-Feeding',
    icon: '/public/assets/skills-icon/Self Feeding.svg',
  },
  {
    skillName: 'Light Household Management',
    icon: '/public/assets/skills-icon/Light Household Management.svg',
  },
  {
    skillName: 'Bed Mobility',
    icon: '/public/assets/skills-icon/Bed Mobility.svg',
  },
  {
    skillName: 'Ambulation',
    icon: '/public/assets/skills-icon/Ambulation.svg',
  },
  {
    skillName: 'Wheelchair Mobility',
    icon: '/public/assets/skills-icon/Wheelchair Mobility.svg',
  },
  {
    skillName: 'Transfers',
    icon: '/public/assets/skills-icon/Transfers.svg',
  },
  {
    skillName: 'Oral Trials',
    icon: '/public/assets/skills-icon/Oral Trials.svg',
  },
  {
    skillName: 'Apraxia Tasks',
    icon: '/public/assets/skills-icon/Apraxia Tasks.svg',
  },
];

export const toCamelCase = (value: string): string => {
  if (!value) {
    return '';
  }

  return value
    .split(/[_\s]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const TherapistTaskList = (props: Props) => {
  const {
    patient,
    handleToRescheduleTask,
    setTaskSelectedId,
    taskSelectedId,
    setOpenTaskDeleteModal,
    setCurrentPatientTask,
    handleOpenTaskNote,
    setIsEditNote,
    handleOpenAddTaskFormFromList,
    handleSpitTask,
    editUnassignedTaskMin,
    selectedDate,
  } = props;
  const skillsMapping = patient.skillName;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isSplitMinActive] = useFeatureToggleWhenReady(isSplitMinEnabled, 'ENTERPRISE');
  const has = useScopeChecker();
  const providerId = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.providerId
  );
  const [isActive, setIsActive] = useState(false);
  const dispatch = useDispatch();
  const open = Boolean(anchorEl);
  const [isAppointmentBook] = useFeatureToggleWhenReady(appointmentBookFlag, 'FACILITY');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const getTherapistSkillListResponse = useSelector(
    (state: any) => state?.getSkillsListReducer?.response?.data
  );

  const skills = skillsMapping?.map((skill: string) => {
    const skills = getTherapistSkillListResponse?.find(
      (skills: any) => skills?.skillName === skill
    );
    return {
      skill_name: skill,
      skill_image: skills?.skillImage,
    };
  });
  const handleSelect = (_e: any, id: any, _patientDetail: any) => {
    if (taskSelectedId.includes(id)) {
      const uuidArr = taskSelectedId.filter((item: any) => item !== id);
      setTaskSelectedId(uuidArr);
    } else {
      setTaskSelectedId((prev: any) => [...prev, id]);
    }
  };

  return (
    <>
      <Stack>
        <Box
          width={'100%'}
          display={'flex'}
          // justifyContent={'space-between'}
          gap={{ lg: 1, md: 1, sm: 1, xs: 1 }}
          alignItems={'center'}
        >
          {' '}
          <Stack
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: 80,
              flexShrink: 0,
            }}
          >
            {patient?.alertType !== 'TASK_ALERT' ? (
              <>
                <DragIndicator sx={{ marginLeft: '10px', color: '#7A7A7A', cursor: 'grab' }} />
                {(!isAppointmentBook ||
                  (patient?.scheduleItemType !== 'APPOINTMENT' &&
                    !patient?.appointmentStartTime)) && (
                  <Checkbox
                    checked={
                      taskSelectedId?.some((taskId: any) => taskId === patient?.uuid) ? true : false
                    }
                    onClick={(e) => handleSelect(e, patient.uuid, patient)}
                    inputProps={
                      {
                        'data-testid': taskTestId(patient?.uuid, 'select'),
                      } as InputHTMLAttributes<HTMLInputElement>
                    }
                  />
                )}
              </>
            ) : null}
          </Stack>
          <Stack
            display={'flex'}
            flexDirection={'row'}
            // gap={{ lg: 0, md: 1, sm: 1, xs: 1 }}
            // Fixed columns (80 + 250 + 68%) summed past the card width on a 13" panel, which
            // is what pushed the list into a horizontal scrollbar. Only the name column keeps a
            // fixed width; the task column takes the remainder and wraps inside itself.
            sx={{ flex: '0 0 140px', minWidth: 0 }}
            gap="10px"
            padding={{ lg: '0', md: '10px 0px', sm: '10px 0px', xs: '10px 0px' }}
          >
            <Stack
              sx={{ width: '100%' }}
              justifyContent={'center'}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: { lg: 'auto', md: 'auto', xs: 'auto', sm: 'auto' },
                }}
              >
                <Stack gap="6px">
                  <Tooltip
                    title={patient?.patientLastname + ', ' + patient?.patientFirstname}
                    placement="top-end"
                  >
                    <Typography
                      sx={{
                        fontSize: '14px',
                        width: '140px',
                        color: '#111927',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontWeight: 500,
                        textAlign: 'left',
                        overflow: 'hidden',
                      }}
                    >
                      {patient?.patientLastname
                        ? patient?.patientLastname + ', ' + patient?.patientFirstname
                        : patient?.patientFirstname}
                    </Typography>
                  </Tooltip>
                  {patient?.source === 'PREADMIT' ? (
                    <Chip
                      variant="outlined"
                      size="small"
                      label="Future Admission"
                      sx={{
                        color: '#FFFFFF',
                        fontFamily: 'Inter',
                        fontSize: '10px',
                        fontWeight: 500,
                        width: 'fit-content',
                        height: '22px',
                        textAlign: 'left',
                        textOverflow: 'ellipsis',
                        borderRadius: '20px',
                        backgroundColor: '#535f62',
                        border: 'none',
                      }}
                    />
                  ) : (
                    <Tooltip
                      title={
                        patient?.payerPlanName && patient?.planTracking
                          ? patient?.payerPlanName + ' - ' + patient?.planTracking
                          : patient?.payerPlanName
                          ? patient?.payerPlanName + '  - Non-PDPM'
                          : 'Non-PDPM'
                      }
                    >
                      <Chip
                        variant="outlined"
                        size="small"
                        label={patient?.payerPlanName}
                        sx={{
                          color: '#111927',
                          fontFamily: 'Inter ',
                          fontSize: '10px ',
                          fontWeight: 500,
                          width: '125px',
                          height: '22px',
                          textAlign: 'left',
                          textOverflow: 'ellipsis',
                          borderRadius: '20px',
                          borderBottom: `2px solid ${getPayerCateGroup(patient?.payerPlanType)
                            ?.borderColor}`,
                          background: `${getPayerCateGroup(patient?.payerPlanType)?.bgColor}`,
                        }}
                      />
                    </Tooltip>
                  )}
                </Stack>
              </Box>
            </Stack>
          </Stack>
          <Stack
            sx={{ flex: '1 1 0', minWidth: 0 }}
            gap="6px"
          >
            {patient?.alertType !== 'TASK_ALERT' ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    rowGap: '6px',
                    marginLeft: { lg: '20px', md: '10px', sm: '10px', xs: '10px' },
                    // Keep clear of the absolutely positioned expand / menu icons (2 × 24 + gaps).
                    paddingRight: '64px',
                  }}
                >
                  {patient?.isSupervisorRequired && (
                    <Box
                      sx={{
                        display: 'inline-block',
                        position: 'relative',
                        mr: 0.5,
                        cursor: 'pointer',
                      }}
                    >
                      <Tooltip
                        title="Supervision required"
                        placement="top"
                      >
                        <WarningAmberRounded sx={{ color: '#FFA500', fontSize: '22px' }} />
                      </Tooltip>
                    </Box>
                  )}
                  <TaskTypeSvg
                    taskType={
                      patient?.scheduleItemType === 'APPOINTMENT' &&
                      isAppointmentBook &&
                      patient?.tasksType === 'EVALUATION'
                        ? 'EVALUATION'
                        : patient?.scheduleItemType === 'APPOINTMENT' &&
                          isAppointmentBook &&
                          patient?.tasksType === 'THERAPY'
                        ? patient?.scheduleItemType
                        : patient?.tasksType?.replace('_', ' ')
                    }
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      marginLeft: { lg: '10px', md: '12px', sm: '22px', xs: '22px' },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 400,
                        color: '#6C737F',
                        textAlign: 'left',
                      }}
                    >
                      {patient?.scheduleItemType === 'APPOINTMENT' && isAppointmentBook
                        ? toCamelCase(patient?.scheduleItemType)
                        : toCamelCase(patient?.tasksType)}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  onClick={() => editUnassignedTaskMin(patient)}
                  data-testid={taskTestId(patient?.uuid, 'minutes')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    marginLeft: { lg: '22px', md: '12px', sm: '22px', xs: '22px' },
                    gap: '5px',
                  }}
                >
                  {patient.overdue ? (
                    <PriorityHigh />
                  ) : patient?.splitRef ? (
                    <Tooltip title="Split Task">
                      <span>
                        <ForkRight sx={{ color: 'rgb(108, 115, 127)' }} />
                      </span>
                    </Tooltip>
                  ) : null}
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      color: 'var(--secondary-main, #384250)',
                      lineHeight: '14px',
                      display: 'inline-block',
                    }}
                  >
                    {patient?.numberOfMinutes} min
                  </Typography>
                  <PencilIcon />
                  <Box
                    component="span"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ display: 'inline-flex', ml: '4px', gap: '4px' }}
                  >
                    {/* THIN-8192: up to two, the latest overdue beside the one due today */}
                    {((patient?.progressNotesDue as ProgressNoteDue[] | undefined) ?? []).map(
                      (due) => (
                        <SchedulerProgressNoteDueGlyph
                          key={due.dueDate}
                          due={due}
                          date={selectedDate ?? ''}
                        />
                      )
                    )}
                  </Box>
                </Box>
              </>
            ) : (
              <Button
                variant="text"
                onClick={() => handleOpenAddTaskFormFromList(patient)}
                data-testid={taskTestId(patient?.uuid, 'add-task')}
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: '500',
                  fontSize: '14px',
                  lineHeight: '22px ',
                  letterSpacing: '0.2px ',
                  color: '#3788d8',
                  mt: '4px',
                }}
                disabled={!has(Permissions.CREATE_DAILY_TASK)}
              >
                + Add Task
              </Button>
            )}
          </Stack>
          <Stack
            flexDirection={'row'}
            sx={{ position: 'absolute', right: '8px', gap: '8px' }}
          >
            <IconButton
              sx={{ padding: '0' }}
              onClick={() => setIsActive((prev: any) => !prev)}
            >
              {!isActive ? <KeyboardArrowDownOutlinedIcon /> : <KeyboardArrowUpOutlinedIcon />}
            </IconButton>
            <IconButton
              sx={{ padding: '0' }}
              onClick={handleClick}
              disabled={patient?.alertType === 'TASK_ALERT'}
              data-testid={taskTestId(patient?.uuid, 'menu')}
            >
              <MoreVertRoundedIcon />
            </IconButton>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
              <MenuItem
                sx={{ gap: '10px', fontWeight: '500', color: '#6C737F' }}
                onClick={() => {
                  handleClose();
                  handleToRescheduleTask(patient);
                }}
                disabled={patient?.source === 'PREADMIT'}
                data-testid={taskTestId(patient?.uuid, 'reschedule')}
              >
                {' '}
                <img
                  alt=""
                  src={calendar}
                />
                Reschedule task
              </MenuItem>
              {isSplitMinActive &&
              has(Permissions.SPLIT_TASK_MINUTES) &&
              patient?.tasksType === 'THERAPY' ? (
                <MenuItem
                  sx={{ gap: '10px', fontWeight: '500', color: '#6C737F' }}
                  onClick={() => {
                    handleClose();
                    handleSpitTask(patient);
                  }}
                  data-testid={taskTestId(patient?.uuid, 'split')}
                  disabled={
                    patient?.splitRef || patient?.source === 'PREADMIT'
                    // || (patient?.scheduleItemType === "APPOINTMENT" && isAppointmentBook)
                  }
                >
                  <img
                    alt=""
                    src={divide}
                  />{' '}
                  Split task mins
                </MenuItem>
              ) : null}
              <MenuItem
                sx={{ gap: '10px', fontWeight: '500', color: '#6C737F' }}
                disabled={
                  patient?.source === 'PREADMIT' && patient?.tasksType === 'THERAPY'
                    ? false
                    : patient?.splitRef
                    ? false
                    : patient?.tasksType === 'GROUP_THERAPY' ||
                      patient?.tasksType === 'THERAPY' ||
                      patient?.tasksType === 'CONCURRENT'
                }
                onClick={() => {
                  handleClose();
                  setCurrentPatientTask(patient?.uuid);
                  setOpenTaskDeleteModal(patient);
                }}
                data-testid={taskTestId(patient?.uuid, 'delete')}
              >
                {' '}
                <img
                  alt=""
                  src={trash}
                />
                Delete task{' '}
              </MenuItem>
              {has(Permissions.ADD_SCHEDULER_NOTE) ? (
                <MenuItem
                  sx={{ gap: '10px', fontWeight: '500', color: '#6C737F' }}
                  onClick={() => {
                    dispatch(resetCreateTherapistTaskNote());
                    dispatch(
                      getTherapistTaskNoteAction({
                        providerId: providerId,
                        taskId: patient?.uuid,
                      })
                    );
                    setIsEditNote(patient?.noteAvailable);
                    setCurrentPatientTask(patient?.uuid);
                    handleClose();
                    handleOpenTaskNote();
                  }}
                  data-testid={taskTestId(patient?.uuid, 'note')}
                >
                  {' '}
                  <AddNoteIcon />
                  {patient?.noteAvailable ? 'Edit a note' : 'Add note'}
                </MenuItem>
              ) : null}
            </Menu>
            {/* <Box
          sx={{
            display: 'flex',
            justifyContent: { lg: 'flex-end', md: 'flex-start' },
            maxWidth: '100px',
            marginRight: '16px',
            cursor: 'pointer',
            marginLeft: { lg: '0', md: '10px', sm: '10px', xs: '10px' },
          }}
          onClick={() => handleToRescheduleTask(patient)}
        >
         
        </Box> */}
          </Stack>
        </Box>
        {isActive && (
          <Stack
            // justifyContent="space-between"
            justifyContent="flex-start"
            flexDirection="row"
            flexWrap="wrap"
            gap={1}
            sx={{ marginLeft: '3.5rem', marginTop: '1rem' }}
          >
            <Stack
              sx={{
                justifyContent: 'center',
                // flex: 1,
                marginRight: '1.5rem',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: '8px',
                  width: { lg: '140px', md: 'auto', xs: 'auto', sm: 'auto' },
                  alignItems: 'center',
                  marginLeft: { lg: '0', md: '45px', sm: '45px', xs: '45px' },
                }}
              >
                {!patient.overdue && patient && skills && skills.length > 0 ? (
                  <AvatarGroup>
                    {skills.map((skill: any, index: number) => {
                      const isSkillsIconPresent =
                        skill?.skill_image && skill?.skill_image?.endsWith('svg');
                      let isIconPresent = null;
                      if (!isSkillsIconPresent) {
                        isIconPresent = skillsList?.find(
                          (skills) => skills?.skillName === skill?.skill_name
                        );
                      }

                      return (
                        <Tooltip
                          key={index}
                          title={skill?.skill_name}
                        >
                          <Avatar
                            sx={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '64px',
                              border: ' 2px solid var(--background-paper, #FFF)',
                              background: 'var(--neutral-100, #F3F4F6)',
                            }}
                            key={index}
                          >
                            {isSkillsIconPresent ? (
                              <img
                                key={skill?.skill_name}
                                width="40px"
                                height="40px"
                                src={skill?.skill_image}
                                alt="not found"
                              />
                            ) : isIconPresent ? (
                              <img
                                key={isIconPresent?.icon}
                                width="40px"
                                height="40px"
                                src={isIconPresent?.icon}
                                alt="not found"
                              />
                            ) : (
                              <DefaultSkillIcon />
                            )}
                          </Avatar>
                        </Tooltip>
                      );
                    })}
                  </AvatarGroup>
                ) : null}
              </Box>
            </Stack>
            <Stack
              sx={{
                justifyContent: 'center',
                marginRight: '1.5rem',
              }}
            >
              <Box
                sx={{
                  display: { md: 'flex', lg: 'block', sm: 'flex', xs: 'flex' },
                  alignItems: 'center',
                  width: { lg: 'auto', md: 'auto', xs: 'auto', sm: 'auto' },
                  marginLeft: { lg: '12px', md: '50px', sm: '50px' },
                }}
              >
                <Typography variant="captionText">Bed Assignment</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, textAlign: 'start' }}>
                  {formatPatientData(
                    patient?.floorNumber,
                    patient?.roomNumber,
                    patient?.bedAssignment
                  )}
                </Typography>
              </Box>
            </Stack>
            {/* <Stack
            sx={{
              justifyContent: 'center',
              marginRight: '1.5rem',
            }}
          >
            <Box
              sx={{
                display: { md: 'flex', lg: 'block', sm: 'flex', xs: 'flex' },
                alignItems: 'center',
                width: { lg: 'auto', md: 'auto', xs: 'auto', sm: 'auto' },
                marginLeft: { lg: '0', md: '50px', sm: '50px' },
              }}
            >
              <Typography variant="captionText">Planned/ Actual Therapy</Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, textAlign: 'start' }}>
                {patient?.plannedActTherapy && patient?.plannedActTherapy}
                {/* 180/260 }
              </Typography>
            </Box>
          </Stack>
          <Stack
            sx={{
              justifyContent: 'center',
              marginRight: '1.5rem',
            }}
          >
            <Box
              sx={{
                display: { md: 'flex', lg: 'block', sm: 'flex', xs: 'flex' },
                alignItems: 'center',
                width: { lg: 'auto', md: 'auto', xs: 'auto', sm: 'auto' },
                marginLeft: { lg: '0', md: '50px', sm: '50px' },
              }}
            >
              <Typography variant="captionText">Eval. Therapists</Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, textAlign: 'start' }}>
                {patient?.evalTherapist &&
                  patient.tasksType !== 'Evaluation' &&
                  patient?.evalTherapist}
                {/* Olivia Smith }
              </Typography>
            </Box>
          </Stack> */}
          </Stack>
        )}
      </Stack>
    </>
  );
};

export default TherapistTaskList;
